import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Check,
  Zap,
  Rocket,
  Crown,
  ArrowRight,
  Loader2,
  Gift,
  Shield,
  Star
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Get started with the basics",
    icon: Shield,
    color: "slate",
    features: [
      "3 free AI responses",
      "Client management",
      "Project tracking",
      "Basic email templates"
    ],
    cta: "Start Free",
    popular: false
  },
  {
    id: "pro",
    name: "Pro",
    price: "$29",
    period: "/month",
    description: "Everything you need to grow",
    icon: Zap,
    color: "sky",
    features: [
      "500 AI generations/month",
      "Contract Builder",
      "Contract Templates",
      "Proof Builder (case studies)",
      "CSV exports for accounting",
      "Priority support"
    ],
    cta: "Start Pro Plan",
    popular: true,
    trial: false
  },
  {
    id: "growth",
    name: "Growth",
    price: "$79",
    period: "/month",
    description: "For scaling freelancers",
    icon: Rocket,
    color: "amber",
    features: [
      "2,000 AI generations/month",
      "Everything in Pro, plus:",
      "Cold Outreach Pro",
      "Video Testimonials",
      "Advanced Analytics",
      "Custom Branding",
      "Affiliate Program",
      "Client Portal"
    ],
    cta: "Start Growth Plan",
    popular: false,
    trial: false
  }
];

export default function PricingPage({ user, onPlanSelected }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get("welcome") === "1";
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoValid, setPromoValid] = useState(null);
  const [promoTier, setPromoTier] = useState(null);

  const handleSelectPlan = async (plan) => {
    if (plan.id === "free") {
      // Free plan - just mark as selected and go to dashboard
      try {
        setLoading("free");
        await api.put("/users/me", { 
          tier: "free",
          needs_plan_selection: false 
        });
        toast.success("Welcome to ClientKit!");
        if (onPlanSelected) onPlanSelected("free");
        navigate("/dashboard");
      } catch (e) {
        toast.error("Something went wrong");
      } finally {
        setLoading(null);
      }
      return;
    }

    // Paid plans - redirect to Stripe-hosted Checkout
    try {
      setLoading(plan.id);
      const res = await api.post("/billing/checkout", {
        tier: plan.id,
        origin_url: window.location.origin
      });

      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Demo accounts can't subscribe — sign in with your real account to upgrade.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to start checkout");
      }
    } finally {
      setLoading(null);
    }
  };

  const handlePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    try {
      const res = await api.get(`/promo/validate/${promoCode.trim()}`);
      if (res.data.valid) {
        setPromoValid(true);
        setPromoTier(res.data.tier);
        toast.success(`Valid code! Grants ${res.data.tier.toUpperCase()} access`);
      } else {
        setPromoValid(false);
        toast.error(res.data.error || "Invalid promo code");
      }
    } catch (e) {
      setPromoValid(false);
      toast.error("Invalid promo code");
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoValid || !promoCode.trim()) return;
    
    try {
      setLoading("promo");
      const res = await api.post("/promo/redeem", { code: promoCode.trim() });
      toast.success(res.data.message || "Promo code applied!");
      if (onPlanSelected) onPlanSelected(res.data.tier);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to redeem code");
    } finally {
      setLoading(null);
    }
  };

  const colorClasses = {
    slate: "border-slate-600 bg-slate-800/30",
    sky: "border-indigo-500 bg-indigo-500/10 ring-2 ring-indigo-500/20",
    amber: "border-indigo-500/50 bg-indigo-500/5"
  };

  const buttonClasses = {
    slate: "bg-slate-700 hover:bg-slate-600 text-white",
    sky: "bg-indigo-500 hover:bg-indigo-600 text-white",
    amber: "bg-indigo-500 hover:bg-indigo-600 text-black"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4" data-testid="pricing-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto text-center mb-12">
        {isWelcome && (
          <div className="inline-flex items-center gap-2 bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-full text-sm mb-6">
            <Gift className="w-4 h-4" />
            Welcome to ClientKit! Choose your plan to get started.
          </div>
        )}
        
        <h1 className="text-3xl sm:text-4xl font-bold text-indigo-500 mb-4">
          Choose Your Plan
        </h1>
        <p className="text-indigo-400 text-lg max-w-2xl mx-auto">
          Start free with 3 AI responses. Upgrade anytime — cancel from your billing dashboard.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          return (
            <Card 
              key={plan.id}
              className={`relative ${colorClasses[plan.color]} transition-all hover:scale-[1.02]`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-indigo-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" />
                    Most Popular
                  </span>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${
                  plan.color === "sky" ? "bg-indigo-500/20" :
                  plan.color === "amber" ? "bg-indigo-500/20" : "bg-slate-400"
                }`}>
                  <Icon className={`w-6 h-6 ${
                    plan.color === "sky" ? "text-indigo-400" :
                    plan.color === "amber" ? "text-indigo-400" : "text-indigo-400"
                  }`} />
                </div>
                <CardTitle className="text-indigo-500 text-xl">{plan.name}</CardTitle>
                <p className="text-indigo-400 text-sm">{plan.description}</p>
              </CardHeader>
              
              <CardContent className="pt-4">
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-indigo-500">{plan.price}</span>
                  <span className="text-indigo-400">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        plan.color === "sky" ? "text-indigo-400" :
                        plan.color === "amber" ? "text-indigo-400" : "text-indigo-400"
                      }`} />
                      <span className="text-slate-400">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loading !== null}
                  className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${buttonClasses[plan.color]} disabled:opacity-50`}
                  data-testid={`select-${plan.id}-btn`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {plan.cta}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Promo Code Section */}
      <div className="max-w-md mx-auto">
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="pt-6">
            <h3 className="text-indigo-500 font-medium mb-3 text-center">Have a promo code?</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setPromoValid(null);
                }}
                placeholder="ENTER CODE"
                className="flex-1 bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2 text-indigo-500 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                data-testid="promo-code-input"
              />
              {promoValid ? (
                <button
                  onClick={handleRedeemPromo}
                  disabled={loading === "promo"}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg font-medium disabled:opacity-50"
                  data-testid="redeem-promo-btn"
                >
                  {loading === "promo" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Redeem"}
                </button>
              ) : (
                <button
                  onClick={handlePromoCode}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium"
                  data-testid="check-promo-btn"
                >
                  Check
                </button>
              )}
            </div>
            {promoValid && promoTier && (
              <p className="text-green-400 text-sm mt-2 text-center">
                ✓ Code grants {promoTier.toUpperCase()} access
              </p>
            )}
            {promoValid === false && (
              <p className="text-red-400 text-sm mt-2 text-center">
                Invalid or expired code
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Skip link for users who want to explore first */}
      <div className="text-center mt-8">
        <button
          onClick={() => handleSelectPlan({ id: "free" })}
          className="text-slate-400 hover:text-indigo-400 text-sm underline"
        >
          Skip for now and start with Free plan
        </button>
      </div>
    </div>
  );
}
